//Third Party Dependencies
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Alert } from 'reactstrap';

//MHD General Purpose libraries
import defaultConfig from '../../config';
import { validateDate } from '../../lib/validation';
import ActiveErrors from '../../components/common/activeErrors';
import { SectionHeading } from '../../components/common/standardComponents';
import { useAppDispatch, useAppSelector } from "../../store";

//Specific Business and UI components
import * as orderManagementActions from '../../actions/orderManagementActions';
import { startBookingProcess, searchPatientFromCareOrders, setActivePatient } from '../../actions/rd2RefactorActions';
import OrderDetailsModal from '../../components/modals/orderDetailsModal';
import { OrderHistoryFilter } from '../../components/orderManagement';
import { OrderHistoryTable } from '../../components/orderManagement';

import { setActiveCareOrderDetails } from '../../actions/careOrderActions';
import { convertStatusCode } from '../../lib/careOrderUtils';

function SearchOrdersView(props: any) {

	const orderManagement: any = useAppSelector((state: any) => state.orderManagement);
	const pageData: any = useAppSelector((state: any) => state.orderManagement.searchCareOrdersPageData);
	const config: any = useAppSelector((state: any) => state.config);
	const activeCareOrder: any = useAppSelector((state: any) => state.careOrder);
	const activePatient: any = useAppSelector((state: any) => state.activePatient);
	const decisionSupportOutput: any = useAppSelector((state: any) => state.decisionSupport);
	const availabilitySearchConfig: any = useAppSelector((state: any) => state.config.availabilitySearch);
	const patientList: any = useAppSelector((state: any) => state.patientSearch.patientList);
	const setActiveCareOrder: any = useAppSelector((state: any) => state.setActiveCareOrder);
	const routePrefix: any = useAppSelector((state: any) => state.config.instance.routePrefix);

	const [dueDateFromIsOpen, setDueDateFromIsOpen] = useState<boolean>(false);
	const [dueDateToIsOpen, setDueDateToIsOpen] = useState<boolean>(false);
	const [dueDateFromValue, setDueDateFromValue] = useState<string>('');
	const [dueDateFromIsValid, setDueDateFromIsValid] = useState<boolean>(true);
	const [dueDateToValue, setDueDateToValue] = useState<string>('');
	const [dueDateToIsValid, setDueDateToIsValid] = useState<boolean>(true);
	const [dueDateSetIsValid, setDueDateSetIsValid] = useState<boolean>(true);
	const [enableEditMode, setEnableEditMode] = useState<boolean>(false);
	const [filterSelectedStatusReasonId, setFilterSelectedStatusReasonId] = useState<number>(0);
	const [isFilterLoaded, setIsFilterLoaded] = useState<boolean>(false);
	const [isFilterOpen, setIsFilterOpen] = useState<boolean>(true);
	const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
	const [isSavingEdit, setIsSavingEdit] = useState<boolean>(false);
	const [isOrderHistoryLoaded, setIsOrderHistoryLoaded] = useState<boolean>(false);
	const [isOverdue, setIsOverdue] = useState<boolean>(false);
	const [lastModifiedDateFromValue, setLastModifiedDateFromValue] = useState<string>('');
	const [lastModifiedDateFromIsOpen, setLastModifiedDateFromIsOpen] = useState<boolean>(false);
	const [lastModifiedDateFromIsValid, setLastModifiedDateFromIsValid] = useState<boolean>(true);
	const [lastModifiedDateToValue, setLastModifiedDateToValue] = useState<string>('');
	const [lastModifiedDateToIsOpen, setLastModifiedDateToIsOpen] = useState<boolean>(false);
	const [lastModifiedDateToIsValid, setLastModifiedDateToIsValid] = useState<boolean>(true);
	const [lastModifiedDateSetIsValid, setLastModifiedDateSetIsValid] = useState<boolean>(true);
	const [memberId, setMemberId] = useState<string>('');
	const [orderDetailsModalVisible, setOrderDetailsModalVisible] = useState<boolean>(false);
	const [orderEntryDateFromValue, setOrderEntryDateFromValue] = useState<string>('');
	const [orderEntryDateFromIsOpen, setOrderEntryDateFromIsOpen] = useState<boolean>(false);
	const [orderEntryDateFromIsValid, setOrderEntryDateFromIsValid] = useState<boolean>(true);
	const [orderEntryDateToValue, setOrderEntryDateToValue] = useState<string>('');
	const [orderEntryDateToIsOpen, setOrderEntryDateToIsOpen] = useState<boolean>(false);
	const [orderEntryDateToIsValid, setOrderEntryDateToIsValid] = useState<boolean>(true);
	const [orderEntryDateSetIsValid, setOrderEntryDateSetIsValid] = useState<boolean>(true);
	const [selectedOrder, setSelectedOrder] = useState<any>({});
	const [selectedOrderStatusDisplay, setSelectedOrderStatusDisplay] = useState<string | null>('');
	const [selectedOrderStatusNotes, setSelectedOrderStatusNotes] = useState<string>('');
	const [selectedSiteId, setSelectedSiteId] = useState<number>(0);
	const [selectedStatusCode, setSelectedStatusCode] = useState<string>('');
	const [selectedStatusReasonId, setSelectedStatusReasonId] = useState<number>(0);
	const [showEditSuccessAlert, setShowEditSuccessAlert] = useState<boolean>(false);
	const [showOrderHistory, setShowOrderHistory] = useState<boolean>(false);

	const dispatch = useAppDispatch();

	useEffect(() => {
		dispatch(orderManagementActions.getSearchCareOrdersPageData())
			.then(() => setIsFilterLoaded(true));
	}, []);

	const onDetailsClick = (e: any, id: number) => {
		e.preventDefault();
		dispatch(orderManagementActions.getCareOrderHistory(id));
		let order = orderManagement.careOrders.find((o: any) => o.id === id);
		resolveStatusDisplayAndVisibility(order);

		setIsSaveDisabled(true);
		setOrderDetailsModalVisible(true);
		setSelectedOrder(order);
		setSelectedOrderStatusNotes(order.notes);
	}

	const handleBookCareOrderVisitClick = (careOrderInfo: any) => {
		let PatientPhoneNumber;
		if (careOrderInfo.patientMobilePhone.trim().length > 0) {
			PatientPhoneNumber = careOrderInfo.patientMobilePhone;
		}
		else if (careOrderInfo.patientAlternatePhone.trim().length > 0) {
			PatientPhoneNumber = careOrderInfo.patientAlternatePhone;
		}
		else if (careOrderInfo.patientHomePhone.trim().length > 0) {
			PatientPhoneNumber = careOrderInfo.patientHomePhone;
		}
		else {
			PatientPhoneNumber = "";
		}
		const patientSearchCriteria = {
			firstName: careOrderInfo.patientFirstName.trim(),
			lastName: careOrderInfo.patientLastName.trim(),
			dateOfBirth: careOrderInfo.patientDateOfBirth,
			memberId: careOrderInfo.patientMemberId,
			identityValue: "",
			excludeWithoutMemberId: false,
			phoneNumber: PatientPhoneNumber,
		};

		dispatch(searchPatientFromCareOrders(patientSearchCriteria))
			.then(() => {
				dispatch(setActivePatient(patientList.items[0].referenceId))
					.then(() => {
						const useDecisionSupport = config.decisionSupport.useDecisionSupport && config.scheduling.useDecisionSupportWithCareOrders;
						dispatch(setActiveCareOrderDetails(careOrderInfo))
							.then(() => {
								dispatch(startBookingProcess(activeCareOrder, decisionSupportOutput, availabilitySearchConfig, props.activePatient.details, useDecisionSupport, routePrefix));
							});
					});
			});
	}

	const onSaveDetails = () => {
		let orderUpdate = {
			id: selectedOrder.id,
			statusCode: selectedStatusCode,
			statusReason: selectedStatusReasonId,
			notes: selectedOrderStatusNotes
		};

		setIsSavingEdit(true);

		dispatch(orderManagementActions.updateCareOrder(orderUpdate))
			.then((response: any) => {
				if (!response.error) {
					setShowEditSuccessAlert(true);
					setEnableEditMode(false);
					setIsSaveDisabled(true);
					setOrderDetailsModalVisible(false);
					setSelectedOrderStatusNotes('');
					setSelectedOrderStatusDisplay('');
					setSelectedStatusCode('');
					setSelectedStatusReasonId(0);
				} else {
					setOrderDetailsModalVisible(false);
				}
			})
			.finally(() => {
				setIsSavingEdit(false);
				setIsOrderHistoryLoaded(false);
				onFilterSearch();
			});
	}

	const onCancelDetails = () => {
		setEnableEditMode(false);
		setIsSaveDisabled(true);
		setOrderDetailsModalVisible(false);
	}

	const onEditClick = (e: any) => {
		setEnableEditMode(!enableEditMode);
		setSelectedStatusCode(selectedOrder.statusCode);
		setSelectedStatusReasonId(selectedOrder.statusReasonId.toString())
	}

	const onStatusListChange = (e: any) => {
		e.preventDefault();
		let selectedStatusCodeValue = e.target.value;
		const filteredStatusReasons = pageData.careOrderStatusReasons.filter((r: any) => r.code === selectedStatusCodeValue);
		setSelectedStatusCode(selectedStatusCodeValue);
		setSelectedStatusReasonId(filteredStatusReasons[0].id.toString())
		resolveSaveDisabled();
	}

	const onStatusReasonListChange = (e: any) => {
		setSelectedStatusReasonId(e.target.value)
		resolveSaveDisabled();
	}

	const onStatusNotesChange = (e: any) => {
		setSelectedOrderStatusNotes(e.target.value);
		resolveSaveDisabled();
	}

	const onFilterChange = (e: any) => {
		if (e.target.name === 'isOverdue') {
			const isOverdueValue = (e.target.checked) ? true : false;
			setIsOverdue(isOverdueValue);
		} else if (e.target.name === 'selectedStatusReasonId') {
			let { value } = e.target;
			setFilterSelectedStatusReasonId(value);
		} else {
			let { name, value } = e.target;
			switch (name) {
				case 'memberId':
					setMemberId(value);
					break;
				case 'selectedSiteId':
					setSelectedSiteId(Number(value));
					break;
				default:
					break;
			}
		}
	}

	const onFilterDateChange = (e: any) => {
		let { name, value } = e.target;
		let isValid = value === "" ? true : validateDate(value);
		switch (name) {
			case 'dueDateFrom':
				setDueDateFromIsValid(isValid);
				setDueDateFromValue(value);
				break;
			case 'dueDateTo':
				setDueDateToIsValid(isValid);
				setDueDateToValue(value);
				break;
			case 'orderEntryDateFrom':
				setOrderEntryDateFromIsValid(isValid);
				setOrderEntryDateFromValue(value);
				break;
			case 'orderEntryDateTo':
				setOrderEntryDateToIsValid(isValid);
				setOrderEntryDateToValue(value);
				break;
			case 'lastModifiedDateFrom':
				setLastModifiedDateFromIsValid(isValid);
				setLastModifiedDateFromValue(value);
				break;
			case 'lastModifiedDateTo':
				setLastModifiedDateToIsValid(isValid);
				setLastModifiedDateToValue(value);
				break;
			default:
				break;
		}
	}

	const validateFilterForm = () => {
		return dueDateFromIsValid && dueDateToIsValid && dueDateSetIsValid
			&& orderEntryDateFromIsValid && orderEntryDateToIsValid && orderEntryDateSetIsValid
			&& lastModifiedDateFromIsValid && lastModifiedDateToIsValid && lastModifiedDateSetIsValid;
	}

	const onFilterSearch = () => {
		let isValid = validateFilterForm();
		if (isValid) {
			setShowOrderHistory(true);
			setIsOrderHistoryLoaded(false);
			setIsFilterOpen(false);

			let searchCriteria = {
				dueDateFrom: dueDateFromValue,
				dueDateTo: dueDateToValue,
				entryDateFrom: orderEntryDateFromValue,
				entryDateTo: orderEntryDateToValue,
				isOverdue: isOverdue,
				lastModifiedDateFrom: lastModifiedDateFromValue,
				lastModifiedDateTo: lastModifiedDateToValue,
				memberId: memberId,
				statusCode: defaultConfig.careOrderStatusCodes.open,
				statusReasonId: filterSelectedStatusReasonId === 0 ? null : filterSelectedStatusReasonId,
				siteId: selectedSiteId === 0 ? null : selectedSiteId,
			}
			dispatch(orderManagementActions.searchCareOrderHistoryAction(searchCriteria))
				.finally(() => {
					setIsOrderHistoryLoaded(true);
				});
		}
	}

	const onFilterReset = () => {
		setMemberId('');
		setFilterSelectedStatusReasonId(0);
		setSelectedSiteId(0);
		setDueDateFromValue('');
		setDueDateFromIsValid(true);
		setDueDateToValue('');
		setDueDateToIsValid(true);
		setDueDateSetIsValid(true);
		setOrderEntryDateFromValue('');
		setOrderEntryDateFromIsValid(true);
		setOrderEntryDateToValue('');
		setOrderEntryDateToIsValid(true);
		setOrderEntryDateSetIsValid(true);
		setLastModifiedDateFromValue('');
		setLastModifiedDateFromIsValid(true);
		setLastModifiedDateToValue('');
		setLastModifiedDateToIsValid(true);
		setLastModifiedDateSetIsValid(true);
		setIsOverdue(false);
	}

	const handleDatepickerChange = (name: string, date: string) => {
		toggleCalendar(name);
		let isValid = date ? true : validateDate(date);
		let formattedDate = moment(date).format("MM/DD/YYYY");

		switch (name) {
			case 'dueDateFrom':
				setDueDateFromIsValid(isValid);
				setDueDateFromValue(formattedDate);
				break;
			case 'dueDateTo':
				setDueDateToIsValid(isValid);
				setDueDateToValue(formattedDate);
				break;
			case 'orderEntryDateFrom':
				setOrderEntryDateFromIsValid(isValid);
				setOrderEntryDateFromValue(formattedDate);
				break;
			case 'orderEntryDateTo':
				setOrderEntryDateToIsValid(isValid);
				setOrderEntryDateToValue(formattedDate);
				break;
			case 'lastModifiedDateFrom':
				setLastModifiedDateFromIsValid(isValid);
				setLastModifiedDateFromValue(formattedDate);
				break;
			case 'lastModifiedDateTo':
				setLastModifiedDateToIsValid(isValid);
				setLastModifiedDateToValue(formattedDate);
				break;
			default:
				break;
		}
	};

	const toggleCalendar = (name: string) => {
		switch (name) {
			case 'dueDateFrom':
				setDueDateFromIsOpen(!dueDateFromIsOpen);
				break;
			case 'dueDateTo':
				setDueDateToIsOpen(!dueDateToIsOpen);
				break;
			case 'orderEntryDateFrom':
				setOrderEntryDateFromIsOpen(!orderEntryDateFromIsOpen);
				break;
			case 'orderEntryDateTo':
				setOrderEntryDateToIsOpen(!orderEntryDateToIsOpen);
				break;
			case 'lastModifiedDateFrom':
				setLastModifiedDateFromIsOpen(!lastModifiedDateFromIsOpen);
				break;
			case 'lastModifiedDateTo':
				setLastModifiedDateToIsOpen(!lastModifiedDateToIsOpen);
				break;
			default:
				break;
		}
	}

	const toggleFilter = () => {
		setIsFilterOpen(!isFilterOpen);
	}

	const dismissEditSuccessAlert = () => {
		setShowEditSuccessAlert(false);
	}

	const resolveSaveDisabled = () => {
		let isSaveOptionDisabled = true;
		if (selectedStatusCode !== selectedOrder.statusCode) {
			isSaveOptionDisabled = false;
		} else if (selectedStatusReasonId !== selectedOrder.statusReasonId) {
			isSaveOptionDisabled = false;
		} else if (selectedOrderStatusNotes) {
			isSaveOptionDisabled = false;
		}
		setIsSaveDisabled(isSaveOptionDisabled);
	}

	const resolveStatusDisplayAndVisibility = (order: any) => {
		let selectedOrderStatusDisplayName: string | null = '';

		switch (order.statusCode.toLowerCase()) {
			case defaultConfig.careOrderStatusCodes.open.toLowerCase():
				selectedOrderStatusDisplayName = convertStatusCode(order.statusCode);
				break;
			case defaultConfig.careOrderStatusCodes.closed.toLowerCase():
				selectedOrderStatusDisplayName = `${convertStatusCode(order.statusCode)} (${order.statusReason})`;
				break;
			default:
				selectedOrderStatusDisplayName = "-";
				break;
		}
		setSelectedOrderStatusDisplay(selectedOrderStatusDisplayName);
	}

	const resolveOpenStatusReasonsForFilter = () => {
		var reasons = pageData.careOrderStatusReasons;
		let results = (reasons && reasons.filter((r: any) => {
			return r.appliesToCareOrder === true && r.code === defaultConfig.careOrderStatusCodes?.open;
		}));
		return results;
	}

	const resolveStatusReasonsForDetails = () => {
		var reasons = pageData.careOrderStatusReasons;
		let results = (reasons && reasons.filter((r: any) => {
			return r.appliesToCareOrderVisit === true;
		}));
		return results;
	}

	let openOrders = orderManagement.careOrders.filter((o: any) => o.statusCode && o.statusCode.toLowerCase() !== defaultConfig.careOrderStatusCodes.closed.toLowerCase()).sort((a: any, b: any) => b.statusCode - a.statusCode);
	let memberIdLabel = config.patient.memberId.fieldLabel;

	let sites = pageData ? pageData.sites : [];
	let careOrderStatuses = pageData ? pageData.careOrderStatuses : [];

	let filterStatusReasons = pageData ? resolveOpenStatusReasonsForFilter() : [];
	let detailStatusReasons = pageData ? resolveStatusReasonsForDetails() : [];

	return (
		<React.Fragment>
			<ActiveErrors />
			<SectionHeading label="Search Open Follow Up Orders" />
			<Alert className="text-center" color="success" isOpen={showEditSuccessAlert} toggle={dismissEditSuccessAlert}>
				SUCCESS! The order edit has been saved.
			</Alert>
			<div>
				<OrderHistoryFilter
					dueDateFromIsOpen={dueDateFromIsOpen}
					dueDateToIsOpen={dueDateToIsOpen}
					dueDateFromValue={dueDateFromValue}
					dueDateFromIsValid={dueDateFromIsValid}
					dueDateToValue={dueDateToValue}
					dueDateToIsValid={dueDateToIsValid}
					dueDateSetIsValid={dueDateSetIsValid}
					handleDatePickerChange={handleDatepickerChange}
					isLoaded={isFilterLoaded}
					isFilterOpen={isFilterOpen}
					isOverdue={isOverdue}
					memberId={memberId}
					memberIdLabel={memberIdLabel}
					memberIdMaxLength={config.patient.memberId.maxLength}
					onChange={onFilterChange}
					onDateChange={onFilterDateChange}
					onSearch={onFilterSearch}
					onReset={onFilterReset}
					lastModifiedDateFromValue={lastModifiedDateFromValue}
					lastModifiedDateFromIsOpen={lastModifiedDateFromIsOpen}
					lastModifiedDateFromIsValid={lastModifiedDateFromIsValid}
					lastModifiedDateToValue={lastModifiedDateToValue}
					lastModifiedDateToIsOpen={lastModifiedDateToIsOpen}
					lastModifiedDateToIsValid={lastModifiedDateToIsValid}
					lastModifiedDateSetIsValid={lastModifiedDateSetIsValid}
					orderEntryDateFromValue={orderEntryDateFromValue}
					orderEntryDateFromIsOpen={orderEntryDateFromIsOpen}
					orderEntryDateFromIsValid={orderEntryDateFromIsValid}
					orderEntryDateToValue={orderEntryDateToValue}
					orderEntryDateToIsOpen={orderEntryDateToIsOpen}
					orderEntryDateToIsValid={orderEntryDateToIsValid}
					orderEntryDateSetIsValid={orderEntryDateSetIsValid}
					selectedSiteId={selectedSiteId}
					selectedStatusReasonId={filterSelectedStatusReasonId}
					siteList={sites}
					statusReasonList={filterStatusReasons}
					toggleCalendar={toggleCalendar}
					toggleFilter={toggleFilter} />
				{showOrderHistory &&
					<OrderHistoryTable
						memberIdLabel={memberIdLabel}
						isLoaded={isOrderHistoryLoaded}
						orders={openOrders}
						onDetailsClick={onDetailsClick}
						showPatientInfo={true}
						handleBookCareOrderVisitClick={handleBookCareOrderVisitClick}
						isParentCreateOrder={false} />
				}
				<OrderDetailsModal
					isEditMode={enableEditMode}
					isOpen={orderDetailsModalVisible}
					isSaveDisabled={isSaveDisabled}
					onCancel={onCancelDetails}
					onConfirm={onSaveDetails}
					onDateBlur={validateDate}
					onEdit={onEditClick}
					onStatusNotesChange={onStatusNotesChange}
					onStatusListChange={onStatusListChange}
					onStatusReasonListChange={onStatusReasonListChange}
					order={selectedOrder}
					orderStatusDisplay={selectedOrderStatusDisplay}
					selectedOrderStatusNotes={selectedOrderStatusNotes}
					selectedStatusCode={selectedStatusCode}
					selectedStatusReasonId={selectedStatusReasonId}
					statusOptionsList={careOrderStatuses}
					statusReasonOptionsList={detailStatusReasons}
					isSavingEdit={isSavingEdit}
					careOrderHistory={orderManagement.careOrderHistory}
					isParentCreateOrder={false} />
			</div>
		</React.Fragment>
	)
}

export default SearchOrdersView;
